import { type JSX, useState } from "react";

import { QuantitySelector } from "common/components/subscriptions";
import Alert from "common/components/ui/Alert";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "common/components/ui/Dialog";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useLicenseMutations from "common/datahooks/useLicenseMutations";
import usePromotions from "common/datahooks/usePromotions";
import useLicensedAccounts from "common/hooks/useLicensedAccounts";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import LicenseAccounts from "./LicenseAccounts";

export default function ManageAgencySeats() {
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const {
    agencyPlan: { unassigned_license_count },
  } = usePromotions();
  const { id: workspaceId, agency_license_count } =
    useSelectedWorkspaceContext();
  const { setLifetimeLicenseCount, isSettingLifetimeLicenseCount } =
    useLicenseMutations();
  const { agencyLicensedAccounts } = useLicensedAccounts(workspaceId);

  const [seatCount, setSeatCount] = useState(agency_license_count);

  async function onSubmit() {
    if (seatCount !== agency_license_count) {
      await setLifetimeLicenseCount({
        workspaceId,
        count: seatCount,
        source: "agency",
        accountsToCancel: [],
      });
    }
    setIsOpen(false);
  }

  let alertComponent: JSX.Element;

  if (seatCount < agencyLicensedAccounts.length) {
    alertComponent = (
      <Alert variant="warning" className="mt-4">
        Cannot remove seats assigned to accounts. Unassign seats to continue
      </Alert>
    );
  } else if (seatCount < agency_license_count) {
    alertComponent = (
      <Alert variant="default" className="mt-4">
        Removing Agency seats will return them to the Agency Plan pool and
        reduce the total number of available seats for all users in this
        workspace
      </Alert>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button size={isDesktop ? "sm" : "md"} className="ml-auto">
          Manage workspace seats
        </Button>
      </DialogTrigger>
      <DialogContent className="md:w-[448px]">
        <DialogTitle className="mb-10">Manage workspace seats</DialogTitle>
        <QuantitySelector
          quantity={seatCount}
          setQuantity={setSeatCount}
          minValue={0}
          maxValue={agency_license_count + unassigned_license_count}
        />
        <div className="flex gap-x-2 mt-4">
          <div className="flex flex-col p-3 border flex-1 gap-y-1 rounded-2xl border-black-200">
            <span className="text-black-500">Assigned</span>
            <div className="flex items-center gap-x-2.5">
              <span className="text-2xl-bold">
                {agencyLicensedAccounts.length}
              </span>
              <LicenseAccounts accounts={agencyLicensedAccounts} />
            </div>
          </div>
          <div className="flex flex-col p-3 border flex-1 gap-y-1 rounded-2xl border-black-200">
            <span className="text-black-500">Available in pool</span>
            <span className="text-2xl-bold text-purple-400">
              {unassigned_license_count}
            </span>
          </div>
        </div>
        {alertComponent}
        <Button
          className="ml-auto mt-10"
          isLoading={isSettingLifetimeLicenseCount}
          disabled={seatCount < agencyLicensedAccounts.length}
          onClick={onSubmit}
        >
          Update seats
        </Button>
      </DialogContent>
    </Dialog>
  );
}
